import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronIcon, FwTrashIcon } from '../../../uiComponents/icons';
import ProductsSoldout from '../../productsSoldout/productsSoldout';
import ProductWeekdays from '../../../data/productsWeekdays.json';

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const EventProductItem = forwardRef(({ item, mode = 'areas', onDelete, defaultExpanded }, ref) => {
  const { t } = useTranslation('translation', { keyPrefix: 'screens.fechasEspeciales.detail.sectionProducts' });
  const [expanded, setExpanded] = useState(defaultExpanded || false);
  const methods = useForm();

  const productName = item.product?.name || '';

  const watchedActive = methods.watch(DAYS.map((d) => `isActive_${d}`));

  useEffect(() => {
    const formData = {};
    const prices = item.dateRangePrices || ProductWeekdays;

    prices.forEach((week) => {
      formData[`price_${week.weekDay}`] = week.price;
      formData[`priceExtraPax_${week.weekDay}`] = week.priceExtraPax;
      formData[`consumption_${week.weekDay}`] = week.consumption;
      formData[`depositPercentage_${week.weekDay}`] = week.depositPercentage;
      formData[`tipPercentage_${week.weekDay}`] = week.tipPercentage;
      formData[`taxPercentage_${week.weekDay}`] = week.taxPercentage;
      formData[`isActive_${week.weekDay}`] = week.isActive;
      formData[`consumptionPerExtraPax_${week.weekDay}`] = week.consumptionPerExtraPax;
      formData[`stock_${week.weekDay}`] = week.stock;
    });

    methods.reset(formData);
  }, [item.productId]);

  useImperativeHandle(ref, () => ({
    validate: () => methods.trigger(),
    getData: () => {
      const data = methods.getValues();
      const commonPrices = (day) => ({
        weekDay: day,
        price: data[`price_${day}`] ? data[`price_${day}`] : 0,
        tipPercentage: data[`tipPercentage_${day}`] ? data[`tipPercentage_${day}`] : 0,
        taxPercentage: data[`taxPercentage_${day}`] ? data[`taxPercentage_${day}`] : 0,
        depositPercentage: data[`depositPercentage_${day}`] ? data[`depositPercentage_${day}`] : 0,
        isActive: data[`isActive_${day}`],
      });

      let dateRangePrices;
      if (mode === 'areas') {
        dateRangePrices = DAYS.map((day) => ({
          ...commonPrices(day),
          priceExtraPax: data[`priceExtraPax_${day}`],
          consumption: data[`consumption_${day}`],
          stock: data[`stock_${day}`],
          consumptionPerExtraPax: data[`consumptionPerExtraPax_${day}`],
        }));
      } else {
        dateRangePrices = DAYS.map((day) => ({
          ...commonPrices(day),
          stock: null,
        }));
      }

      return { productId: item.productId, dateRangePrices };
    },
    expand: () => setExpanded(true),
  }));

  const DAY_ORDER = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
  const pricesData = (item?.dateRangePrices || ProductWeekdays)
    .slice()
    .sort((a, b) => (DAY_ORDER[a.weekDay] ?? 7) - (DAY_ORDER[b.weekDay] ?? 7));

  return (
    <div className='fe-product-item'>
      <div className='fe-product-header' onClick={() => setExpanded(!expanded)}>
        <div className='fe-product-header-info'>
          <span className='fe-product-header-name'>{productName}</span>
          <div className='fe-product-header-days'>
            {DAYS.map((day, i) => (
              <span
                key={day}
                className={`fe-day-dot ${watchedActive[i] ? 'fe-day-dot--active' : ''}`}
                title={t(`day.${day}`)}
              >
                {t(`dayShort.${day}`)}
              </span>
            ))}
          </div>
        </div>
        <div className='fe-product-header-actions'>
          <button
            type='button'
            className='fe-btn-delete'
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id);
            }}
          >
            <FwTrashIcon color='#98A2B3' fontSize='16px' />
          </button>
          <span className={`fe-chevron ${expanded ? 'fe-chevron-open' : ''}`}>
            <ChevronIcon rotation={90} size='16px' color='var(--color-secondary)' />
          </span>
        </div>
      </div>

      {expanded && (
        <div className='fe-product-body'>
          <FormProvider {...methods}>
            <div className='fe-table-wrapper'>
              <ProductsSoldout mode={mode} methods={methods} data={pricesData} />
            </div>
          </FormProvider>
        </div>
      )}
    </div>
  );
});

EventProductItem.displayName = 'EventProductItem';

export default EventProductItem;
